// ─── Single source of truth for all city landing page content ────────────────
// Shape: { name, slug, bookingUrl, zips, seo, hero, problem, local, beforeAfter, reviews }

export const westAshley = {
  name: 'West Ashley',
  slug: 'rug-cleaning-west-ashley',
  bookingUrl: null, // TODO: add HouseCall Pro direct link when available
  zips: ['29407', '29414'],
  seo: {
    title: 'Rug Cleaning West Ashley SC | The Islands Rug Spa',
    description: 'Professional rug, carpet & upholstery cleaning in West Ashley, SC (29407, 29414). Owner-operated, IICRC-certified. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning in West Ashley, SC',
    subheadline: 'Professional rug, carpet & upholstery cleaning trusted by West Ashley families.',
  },
  problem: {
    headline: 'Your Rugs Look Fine. But Do They Smell Fine?',
    body: "West Ashley homes breathe humidity year-round. Under those oak canopies along Glenn McConnell and the Ashley River corridor, moisture seeps into carpet fibers and rug backing — slowly. You stop noticing the musty undertone because you're home every day. Guests notice it the moment they walk in. Dust mites, mold spores, and pet dander build up invisibly in the fibers of your most-used rugs. Regular vacuuming moves surface debris — it doesn't touch what's embedded deep in the pile.",
    closer: "That's where we come in. One professional deep clean removes what years of vacuuming can't — and your home smells the way it should.",
  },
  local: {
    headline: 'We Know West Ashley Homes',
    body: "West Ashley's mix of 1970s ranches, newer subdivisions off Bees Ferry, and craftsman homes near Avondale all share one thing: they're lived-in homes with real rugs that take real abuse. We understand the humidity challenges along the Ashley River corridor, the sandy soil that tracks in from back yards, and the pet-friendly households that are the norm out here. We're a short drive away and treat every home like a neighbor's — because it is.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Area rug restoration — West Ashley living room' },
  ],
  reviews: [
    {
      name: 'Patricia Lawson',
      location: 'Avondale, West Ashley',
      stars: 5,
      text: "I have two labs and a wool area rug I thought was done for. They got out stains I'd given up on and the rug smells completely fresh. Worth every penny.",
    },
    {
      name: 'Robert Hines',
      location: 'West Ashley',
      stars: 5,
      text: "Owner-operated means they actually care. Showed up on time, walked me through everything, and my carpets look better than when we moved in three years ago.",
    },
    {
      name: 'Dana Fitch',
      location: 'Carolina Bay',
      stars: 5,
      text: "Our sectional sofa had mystery stains from the kids. Now it looks brand new. Fast, professional, and no hard sell. Highly recommend.",
    },
  ],
};

export const jamesIsland = {
  name: 'James Island',
  slug: 'rug-cleaning-james-island',
  bookingUrl: null,
  zips: ['29412'],
  seo: {
    title: 'Rug Cleaning James Island SC | The Islands Rug Spa',
    description: 'Professional rug, carpet & upholstery cleaning on James Island, SC (29412). Coastal specialists. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning on James Island, SC',
    subheadline: 'Coastal-savvy rug, carpet & upholstery cleaning for James Island homes.',
  },
  problem: {
    headline: 'Salt Air and Humidity Are Working Against Your Rugs',
    body: "Living near the James Island waterways is incredible — until you look at what salt air, constant humidity, and tidal marsh conditions are doing to your rugs and upholstery. Sand works into rug fibers like sandpaper, breaking down the pile from the inside out. Salt air accelerates odor buildup in carpet backing. The moisture from two inches of rain on a Tuesday afternoon never fully leaves an unventilated room.",
    closer: "You're not just cleaning for appearance — you're protecting real investments from the coastal environment that makes this place so desirable.",
  },
  local: {
    headline: 'Coastal Cleaning for a Coastal Community',
    body: "James Island homes have unique cleaning needs that generic national chains don't understand. Between the beach sand tracked into everything, the tidal humidity that clings to fabric, and homes that sit close to the water, your rugs need specialists who know this environment. We've cleaned hundreds of coastal Lowcountry homes and know exactly how to treat salt-damaged fibers and eliminate that stubborn humid-day smell.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Carpet restoration — James Island home' },
  ],
  reviews: [
    {
      name: 'Tom Burgess',
      location: 'James Island',
      stars: 5,
      text: "The salt air out here is brutal on fabric. My living room rug had that damp, musty smell I couldn't shake. One treatment and it's completely gone. Wish I'd called sooner.",
    },
    {
      name: 'Angela Moore',
      location: 'James Island',
      stars: 5,
      text: "Professional, kind, and did an absolutely incredible job on my couch. I have two cats and I was embarrassed to have people over. Not anymore.",
    },
    {
      name: 'Karen Nettles',
      location: 'James Island',
      stars: 5,
      text: "Best cleaning service I've found on the island. On time, efficient, and the results were far better than I expected. Will absolutely use again.",
    },
  ],
};

export const follyBeach = {
  name: 'Folly Beach',
  slug: 'rug-cleaning-folly-beach',
  bookingUrl: null,
  zips: ['29439'],
  seo: {
    title: 'Rug Cleaning Folly Beach SC | The Islands Rug Spa',
    description: 'Rug, carpet & upholstery cleaning for Folly Beach, SC (29439). Vacation rental specialists. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning on Folly Beach, SC',
    subheadline: 'Rug, carpet & upholstery cleaning built for beach homes and vacation rentals.',
  },
  problem: {
    headline: 'Sand. Salt Air. Repeat Guests. Your Rugs Take the Hit.',
    body: "Folly Beach is one of the most lived-in, loved-on stretches of coast in South Carolina — and that constant use shows up in your rugs. Beach sand embeds in carpet pile with every barefoot step. Salt air penetrates fabric backing and breeds odor. Vacation rentals cycle through dozens of guests a season, each one grinding in what the last left behind. Between-guest vacuuming is maintenance — it isn't cleaning.",
    closer: "Professional deep cleaning removes what vacuums can't reach and makes your property smell fresh for every new arrival.",
  },
  local: {
    headline: 'Built for Beach House Turnover',
    body: "Folly Beach homes — whether primary residences or vacation rentals — have specific cleaning rhythms that standard services don't accommodate. We understand seasonal turnover schedules, salt air damage to natural-fiber rugs, and the wear patterns that come from beach-traffic homes. We work around your rental calendar and get the job done right, every time.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Vacation rental rug cleaning — Folly Beach' },
  ],
  reviews: [
    {
      name: 'Claire Simmons',
      location: 'Folly Beach',
      stars: 5,
      text: "We rent our beach house seasonally and getting it cleaned between guests was always stressful. These guys are fast, thorough, and the rugs look fresh for every new arrival.",
    },
    {
      name: 'Mike Chen',
      location: 'Folly Beach',
      stars: 5,
      text: "My entire first floor carpet looks new. Worth every dollar — especially given how quickly things get worn down out here.",
    },
    {
      name: 'Liz Brandt',
      location: 'Folly Beach',
      stars: 5,
      text: "Quick turnaround between rental guests, great results, fair price. Exactly what you need when you're managing a beach property.",
    },
  ],
};

export const downtown = {
  name: 'Downtown Charleston',
  slug: 'rug-cleaning-downtown-charleston',
  bookingUrl: null,
  zips: ['29401', '29403'],
  seo: {
    title: 'Rug Cleaning Downtown Charleston SC | The Islands Rug Spa',
    description: 'Expert rug, carpet & upholstery cleaning for historic Downtown Charleston (29401, 29403). Heirloom rug specialists. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning in Downtown Charleston, SC',
    subheadline: 'White-glove rug, carpet & upholstery care for historic Peninsula homes.',
  },
  problem: {
    headline: 'Historic Homes Demand More Than a Vacuum',
    body: "Downtown Charleston's historic homes are some of the most beautiful in the country — and some of the most demanding to maintain. Antique Persian and Oriental rugs common to South of Broad townhomes absorb decades of humidity, foot traffic, and the fine dust that settles in 100-year-old plaster walls. Peninsula homes use rugs as anchors for entire room designs; when those rugs look tired, the whole space suffers. High-end wool, silk, and heirloom rugs require specialized care that general carpet cleaners simply don't have the training to provide.",
    closer: "We do. Every piece is assessed individually and cleaned with the method it deserves.",
  },
  local: {
    headline: 'Trusted by Discerning Charleston Homeowners',
    body: "Downtown Peninsula clients expect a different level of care — discretion, expertise, and respect for valuable, often irreplaceable textiles. We are IICRC-certified specialists who understand the difference between a machine-made area rug and a hand-knotted Persian that's been in a family for three generations. We treat every piece as the investment it is. No cookie-cutter steam cleaning — proper care for proper rugs.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Antique rug restoration — Downtown Charleston' },
  ],
  reviews: [
    {
      name: 'Margaret Bates',
      location: 'South of Broad',
      stars: 5,
      text: "My family's antique Tabriz rug is irreplaceable. I was nervous entrusting it to anyone. They were knowledgeable, careful, and the results were stunning. I'll never use anyone else.",
    },
    {
      name: 'William Pryor',
      location: 'Harleston Village',
      stars: 5,
      text: "Our historic condo has original hardwood throughout and several fine rugs. They cleaned everything beautifully and were completely professional from start to finish.",
    },
    {
      name: 'Suzanne Delacroix',
      location: 'Cannonborough',
      stars: 5,
      text: "My velvet sofa was starting to show its age. They brought it back to life. The color is vibrant and the fabric feels like new. Exceptional craftsmanship.",
    },
  ],
};

export const johnsIsland = {
  name: 'Johns Island',
  slug: 'rug-cleaning-johns-island',
  bookingUrl: null,
  zips: ['29455'],
  seo: {
    title: 'Rug Cleaning Johns Island SC | The Islands Rug Spa',
    description: 'Local rug, carpet & upholstery cleaning in Johns Island, SC (29455). Based on Maybank Hwy. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning on Johns Island, SC',
    subheadline: 'Professional rug, carpet & upholstery cleaning — right in your backyard.',
  },
  problem: {
    headline: "Johns Island Is Growing Fast. Your Rugs Are Showing It.",
    body: "The newer subdivisions on Johns Island are beautiful — but construction dust still settles into every home in the area, and the rural roads mean muddy boots and sandy paws tracking in constantly. Add in the creeks, marshes, and humidity that make Johns Island the Lowcountry gem it is, and you have the perfect recipe for embedded dirt, moisture, and allergens in your carpets and rugs. New carpets look great for about a year before that Lowcountry reality sets in.",
    closer: "We help you extend that look — and protect your investment — for years longer.",
  },
  local: {
    headline: "Based in Johns Island, Cleaning Johns Island Homes",
    body: "Our business is based right here on Johns Island (Maybank Hwy). We're not a franchise driving in from across the bridge — we're your neighbors. We understand the dusty driveways, the creek-side moisture, and the mix of new construction and established family homes that make this island so unique. When you call us, you get the owner — someone who cares about this community.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Carpet cleaning — Johns Island home' },
  ],
  reviews: [
    {
      name: 'Eleanor Davidson',
      location: 'Johns Island',
      stars: 5,
      text: "Finally, textile care specialists who understand the unique challenges of Lowcountry living. My silk carpets have never looked better. Impeccable craftsmanship.",
    },
    {
      name: 'Kevin Marsh',
      location: 'Johns Island',
      stars: 5,
      text: "We have three dogs and light-colored carpet — a disaster combination. They got it looking better than I thought was possible. Will be a regular customer.",
    },
    {
      name: 'Sarah Colleton',
      location: 'Johns Island',
      stars: 5,
      text: "Super responsive, showed up exactly when they said, did an outstanding job. Love supporting a local business that's this professional.",
    },
  ],
};

export const kiawah = {
  name: 'Kiawah Island',
  slug: 'rug-cleaning-kiawah-island',
  bookingUrl: null,
  zips: ['29455'],
  seo: {
    title: 'Rug Cleaning Kiawah Island SC | The Islands Rug Spa',
    description: 'White-glove rug, carpet & upholstery cleaning on Kiawah Island, SC. IICRC-certified coastal specialists. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning on Kiawah Island, SC',
    subheadline: 'White-glove rug, carpet & upholstery care for Kiawah Island resort homes.',
  },
  problem: {
    headline: 'Your Island Home Deserves Island-Caliber Care',
    body: "Kiawah Island homes are among the most beautiful in the Lowcountry — and among the most exposed to the elements that damage fine textiles. Oceanic salt air penetrates rug fibers and upholstery backing, accelerating odor and fiber degradation. Beach sand, tracked in from the Ocean Course or the shoreline, grinds into pile with every step. Vacation homes see intense, concentrated use — full occupancy for weeks, then sitting dormant in sea air for months.",
    closer: "By the time the smell becomes noticeable, the damage is well underway. Early professional care protects the investment.",
  },
  local: {
    headline: 'Trusted by Kiawah Homeowners for Years',
    body: "Island homes at this level require a provider who understands both luxury textiles and the coastal environment. We have cleaned fine Persian and Oriental rugs, high-end wool and silk carpets, and custom upholstery for primary and vacation residences throughout Kiawah. We're fully insured, IICRC-certified, and accustomed to the discretion and standards that island community residents expect.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Oriental rug restoration — Kiawah Island' },
  ],
  reviews: [
    {
      name: 'Margaret Whitmore',
      location: 'Kiawah Island',
      stars: 5,
      text: "The Islands treated my antique Persian rug with the reverence it deserved. After years of salt air exposure, it looks magnificent again. Their understanding of coastal conditions is unmatched.",
    },
    {
      name: 'Helen Carver',
      location: 'Kiawah Island',
      stars: 5,
      text: "I've tried other services out here before. None of them understood fine rugs the way these specialists do. The difference in results is remarkable.",
    },
    {
      name: 'Peter Fontaine',
      location: 'Kiawah Island',
      stars: 5,
      text: "Exceptional service from start to finish. They handled our wool rugs with genuine expertise and left everything looking pristine.",
    },
  ],
};

export const isleOfPalms = {
  name: 'Isle of Palms',
  slug: 'rug-cleaning-isle-of-palms',
  bookingUrl: null,
  zips: ['29451'],
  seo: {
    title: 'Rug Cleaning Isle of Palms SC | The Islands Rug Spa',
    description: 'Professional rug, carpet & upholstery cleaning on Isle of Palms, SC (29451). Barrier island specialists. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning on Isle of Palms, SC',
    subheadline: 'Expert rug, carpet & upholstery cleaning built for the barrier island life.',
  },
  problem: {
    headline: "The Ocean Is Beautiful. What It Does to Your Rugs Isn't.",
    body: "Life on IOP is as good as it gets — but the barrier island environment is relentless on textiles. Salt air, humidity that routinely tops 90%, sand that finds its way into every room, and UV exposure from bright coastal light all conspire to age rugs and upholstery faster than anywhere else in the Lowcountry. Vacation rental homes turn over constantly, accumulating heavy foot traffic and embedded grime between seasons.",
    closer: "The ocean doesn't have to win. Professional cleaning protects your investment and keeps your home feeling like a retreat.",
  },
  local: {
    headline: 'Barrier Island Specialists, Not Mainland Generalists',
    body: "We understand barrier island homes because we've cleaned hundreds of them. The combination of high humidity, salt air, and constant sandy foot traffic requires specific techniques that generic carpet cleaners don't carry. Whether it's a high-turnover vacation rental on Palm Blvd or a primary residence, we bring the expertise to restore your textiles and protect them from the coastal environment.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Carpet cleaning — Isle of Palms vacation home' },
  ],
  reviews: [
    {
      name: 'Drew Harrington',
      location: 'Isle of Palms',
      stars: 5,
      text: "Run a short-term rental on IOP. Between guests the rugs and couch always look rough. These guys turned everything around fast and the results were great every time.",
    },
    {
      name: 'Frances Wentworth',
      location: 'Isle of Palms',
      stars: 5,
      text: "My house is my sanctuary. The rug in the living room had taken on that salt-air smell. One cleaning and it's completely fresh. Can't believe I waited so long.",
    },
    {
      name: 'Gail Thornton',
      location: 'Isle of Palms',
      stars: 5,
      text: "Fast, professional, and fantastic results. The carpet in our beach house looks brand new. Highly recommend to any IOP homeowner.",
    },
  ],
};

export const sullivansIsland = {
  name: "Sullivan's Island",
  slug: 'rug-cleaning-sullivans-island',
  bookingUrl: null,
  zips: ['29482'],
  seo: {
    title: "Rug Cleaning Sullivan's Island SC | The Islands Rug Spa",
    description: "Professional rug, carpet & upholstery cleaning on Sullivan's Island, SC (29482). Coastal specialists. Free estimate.",
  },
  hero: {
    headline: "Rug Cleaning on Sullivan's Island, SC",
    subheadline: "Expert rug, carpet & upholstery care for Sullivan's Island homes.",
  },
  problem: {
    headline: "Sullivan's Island Homes Are Stunning. The Salt Air Is Relentless.",
    body: "Sullivan's Island is one of the most desirable addresses in the Lowcountry — and one of the most demanding environments for textiles. The combination of salt air off the Atlantic, high year-round humidity, and beach sand in every room creates conditions that degrade carpet fibers, embed odor in upholstery backing, and age fine rugs long before their time. Primary residences and vacation homes alike feel the effect within months of purchase.",
    closer: "One professional deep clean reverses what the elements have been building for years.",
  },
  local: {
    headline: "Island-Grade Care for Island-Grade Homes",
    body: "Sullivan's Island properties are distinctive — architecturally, historically, and in their cleaning needs. We've serviced fine rugs and upholstery in many of the island's homes and understand exactly what the coastal environment demands from both cleaning techniques and products. We show up professionally, work carefully, and leave your home better than we found it.",
  },
  beforeAfter: [
    { before: null, after: null, caption: "Rug cleaning — Sullivan's Island residence" },
  ],
  reviews: [
    {
      name: 'Frances Wentworth',
      location: "Sullivan's Island",
      stars: 5,
      text: "My Sullivan's house is my sanctuary. The rug in the living room had taken on that salt-air smell. One cleaning and it's completely fresh. Can't believe I waited so long.",
    },
    {
      name: 'James Beaumont',
      location: "Sullivan's Island",
      stars: 5,
      text: "They treated our historic home's Persian rugs with real expertise. Understood the value of what they were handling. Outstanding results.",
    },
    {
      name: 'Caroline Rhett',
      location: "Sullivan's Island",
      stars: 5,
      text: "Discreet, professional, and exceptional work. Exactly what you want from a service provider on the island.",
    },
  ],
};

export const mountPleasant = {
  name: 'Mount Pleasant',
  slug: 'rug-cleaning-mount-pleasant',
  bookingUrl: null,
  zips: ['29464'],
  seo: {
    title: 'Rug Cleaning Mount Pleasant SC | The Islands Rug Spa',
    description: 'Professional rug, carpet & upholstery cleaning in Mount Pleasant, SC (29464). Family-safe, IICRC-certified. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning in Mount Pleasant, SC',
    subheadline: 'Professional rug, carpet & upholstery cleaning for Mount Pleasant families.',
  },
  problem: {
    headline: "Mount Pleasant Families Are Hard on Rugs. (That's a Good Thing.)",
    body: "Mount Pleasant is a place people actually live — active families, kids' sports gear coming through the garage, dogs that have claimed the best spot on the couch. All of that living accumulates in your carpets and rugs in ways a vacuum can't address. The tidal marshes along Shem Creek and the Wando River mean the air holds moisture that quietly embeds itself in fabric fibers, contributing to indoor air quality issues your family breathes every day.",
    closer: "A true deep clean changes that — and costs a fraction of what premature carpet replacement does.",
  },
  local: {
    headline: 'Serving Mount Pleasant Families the Right Way',
    body: "Mount Pleasant homeowners have high expectations — and we meet them. We're locally owned, IICRC-certified, and we treat your home with the same respect you do. No high-pressure sales, no surprise charges, no corners cut. Just thorough, expert cleaning using eco-friendly solutions that are safe for your kids and pets. We've built our reputation in this community one home at a time.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Carpet deep clean — Mount Pleasant home' },
  ],
  reviews: [
    {
      name: 'Becky Norris',
      location: 'Old Village, Mount Pleasant',
      stars: 5,
      text: "Four kids and two golden retrievers — my carpets were a disaster. They did an unbelievable job. The house smells completely fresh and the carpets look new. Booked them again already.",
    },
    {
      name: 'Greg Tillman',
      location: 'Snee Farm',
      stars: 5,
      text: "Used them for a large Persian rug in our dining room. Meticulous work, great communication, and fair pricing. Exactly what you want from a local business.",
    },
    {
      name: 'Carol Ann Simms',
      location: 'Mount Pleasant',
      stars: 5,
      text: "They cleaned our sectional and area rug the day before Thanksgiving. Showed up on time, worked quickly, and the results were perfect. Lifesavers.",
    },
  ],
};

export const northMountPleasant = {
  name: 'North Mount Pleasant',
  slug: 'rug-cleaning-north-mount-pleasant',
  bookingUrl: null,
  zips: ['29466'],
  seo: {
    title: 'Rug Cleaning North Mount Pleasant SC | The Islands Rug Spa',
    description: 'Rug, carpet & upholstery cleaning in North Mount Pleasant, SC (29466) — Park West, Rivertowne & beyond. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning in North Mount Pleasant, SC',
    subheadline: 'Rug, carpet & upholstery cleaning for Park West, Rivertowne, and surrounding communities.',
  },
  problem: {
    headline: "New Homes Get Dirty Fast Out Here. Here's Why.",
    body: "North Mount Pleasant's newer subdivisions — Park West, Rivertowne, RiverGate — have some of the nicest homes in the Charleston area. But new construction means ongoing dust that settles into carpet fibers for months. Growing families and active kids mean rugs take a beating quickly. And the same Lowcountry humidity that affects the rest of the region doesn't spare North Mount Pleasant — moisture in fabric means allergens, odors, and gradual fiber breakdown.",
    closer: "Don't wait until it's visible. The worst buildup is the kind you can't see yet.",
  },
  local: {
    headline: 'Expert Cleaning for Growing North Mount Pleasant Families',
    body: "The North Mount Pleasant communities are full of families who care about their homes and expect professional-grade results. We bring IICRC-certified expertise with the personal service of a local, owner-operated business. Eco-friendly cleaning solutions safe for kids and pets — still tough on embedded grime and allergens that standard vacuuming misses. We come to you and leave your home looking its best.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Area rug cleaning — Park West home' },
  ],
  reviews: [
    {
      name: 'Jenna Polk',
      location: 'Park West',
      stars: 5,
      text: "We moved into a house with light carpets and a dog. I was dreading what they looked like under the furniture. After cleaning — stunning. I couldn't believe it.",
    },
    {
      name: 'Chris Avalon',
      location: 'Rivertowne',
      stars: 5,
      text: "Quick to respond, professional, and did a perfect job on our living room rug. Even the deep-set stains from three years of kids came out. Highly recommend.",
    },
    {
      name: 'Melissa Garrett',
      location: 'North Mount Pleasant',
      stars: 5,
      text: "Used them twice now. Both times showed up on time, did excellent work, and left the place spotless. Best local cleaning service we've found.",
    },
  ],
};

export const danielIsland = {
  name: 'Daniel Island',
  slug: 'rug-cleaning-daniel-island',
  bookingUrl: null,
  zips: ['29492'],
  seo: {
    title: 'Rug Cleaning Daniel Island SC | The Islands Rug Spa',
    description: 'Professional rug, carpet & upholstery cleaning on Daniel Island, SC (29492). IICRC-certified. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning on Daniel Island, SC',
    subheadline: "Premium rug, carpet & upholstery cleaning for Daniel Island's master-planned community.",
  },
  problem: {
    headline: 'Daniel Island Homes Look Immaculate. Until You Look Closer.',
    body: "Daniel Island is one of the most meticulously planned communities in the Southeast. The homes look great — and residents want to keep them that way. But even the best-maintained homes accumulate what vacuuming can't reach: fine particulate from the surrounding marsh, pet dander, allergens, and the slow effect of Lowcountry humidity on carpet and rug fibers. The dirt you can see is the easy part.",
    closer: "A truly clean home requires getting to what's embedded below the surface, and that takes professional equipment and expertise.",
  },
  local: {
    headline: 'The Right Standard for Daniel Island Homes',
    body: "Daniel Island homeowners have high standards, and so do we. We're IICRC-certified specialists trained to handle everything from everyday carpet cleaning to fine Oriental and wool rugs. Our process is thorough, eco-friendly, and delivered with the professionalism this community expects. No high-pressure tactics, no hidden charges — just excellent results.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Full-home cleaning — Daniel Island residence' },
  ],
  reviews: [
    {
      name: 'Allen Bradford',
      location: 'Daniel Island',
      stars: 5,
      text: "Impeccable service. Called on a Tuesday, they were at our house Thursday morning. The rugs and carpets throughout our home look absolutely perfect.",
    },
    {
      name: 'Susan Park',
      location: 'Daniel Island',
      stars: 5,
      text: "We entertain a lot and our rugs get heavy use. They make them look showroom-perfect every single time. Reliable, professional, the best results I've seen.",
    },
    {
      name: 'David Conley',
      location: 'Daniel Island',
      stars: 5,
      text: "My kids destroyed the family room carpet. Stains I thought were permanent are completely gone. The owner clearly takes real pride in his work.",
    },
  ],
};

export const charlestonMetro = {
  name: 'Charleston',
  slug: 'rug-cleaning-charleston',
  bookingUrl: null,
  zips: ['29401','29403','29405','29407','29412','29414','29418','29439',
         '29451','29455','29464','29466','29483','29485','29486','29487','29492'],
  seo: {
    title: 'Rug Cleaning Charleston SC | The Islands Rug Spa',
    description: 'Professional rug, carpet & upholstery cleaning across greater Charleston, SC. Free pickup & delivery. Owner-operated, IICRC-certified. Free estimate.',
  },
  hero: {
    headline: 'Rug Cleaning for Charleston Homes',
    subheadline: 'We pick up, deep clean, and deliver back to your door. Free estimate, no obligation.',
  },
  problem: {
    headline: "Most Charleston Homeowners Don't Realize How Much Their Rugs Are Holding",
    body: "Years of dirt, allergens, and odors accumulate in the fibers of your most-used rugs — and vacuuming never touches them. The Lowcountry climate makes it worse: humidity embeds moisture deep in rug backing, salt air accelerates odor, and the fine dust that settles in every Charleston home works into carpet pile like sandpaper over time. You stop noticing because you're home every day. Guests notice the moment they walk in.",
    closer: "One professional deep clean removes what years of regular maintenance can't — and your home smells the way it should.",
  },
  local: {
    headline: 'Charleston-Based. Owner on Every Job.',
    body: "The Islands Rug Spa is an owner-operated business based right here on Johns Island. We serve the entire greater Charleston metro — Summerville to Folly Beach, Kiawah to Daniel Island — and we treat every home like a neighbor's, because it is. No franchise. No rotating crew. The owner shows up, assesses your rugs personally, and does the work. We pick up, deep clean at our facility, and deliver back to your door.",
  },
  beforeAfter: [
    { before: null, after: null, caption: 'Area rug restoration — Charleston home' },
  ],
  reviews: [
    {
      name: 'Patricia Lawson',
      location: 'West Ashley',
      stars: 5,
      text: "I have two labs and a wool area rug I thought was done for. They got out stains I'd given up on and the rug smells completely fresh. Worth every penny.",
    },
    {
      name: 'Allen Bradford',
      location: 'Daniel Island',
      stars: 5,
      text: "Called on a Tuesday, they were at our house Thursday morning. The rugs and carpets throughout our home look absolutely perfect. Impeccable service.",
    },
    {
      name: 'Becky Norris',
      location: 'Mount Pleasant',
      stars: 5,
      text: "Four kids and two golden retrievers — my carpets were a disaster. They did an unbelievable job. The house smells completely fresh and the carpets look new. Booked them again already.",
    },
  ],
};

// ─── Master list for nav dropdown and footer ─────────────────────────────────
export const allCities = [
  westAshley,
  jamesIsland,
  follyBeach,
  downtown,
  johnsIsland,
  kiawah,
  isleOfPalms,
  sullivansIsland,
  mountPleasant,
  northMountPleasant,
  danielIsland,
];
